import React, { useState, useEffect, useCallback } from 'react';
import { adminTestDriveService } from '../../services/adminService'; // ← fixed: exported from adminService.js

// ─── Status helpers ───────────────────────────────────────────────────────────
const STATUS_META = {
  requested:  { color: '#f59e0b', bg: '#fef3c720', label: 'Requested' },
  confirmed:  { color: '#3b82f6', bg: '#eff6ff20', label: 'Confirmed' },
  completed:  { color: '#10b981', bg: '#d1fae520', label: 'Completed' },
  cancelled:  { color: '#ef4444', bg: '#fee2e220', label: 'Cancelled' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { color: '#6b7280', bg: '#f3f4f620', label: status };
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 700,
      textTransform: 'capitalize',
      background: meta.bg,
      color: meta.color,
      border: `1px solid ${meta.color}40`,
      display: 'inline-block',
      whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
};

const ModeBadge = ({ mode }) => (
  <span style={{
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'capitalize',
    background: mode === 'home' ? '#ede9fe' : '#e0f2fe',
    color: mode === 'home' ? '#7c3aed' : '#0369a1',
  }}>
    {mode === 'home' ? '🏠 Home' : '🏢 Hub'}
  </span>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, color, bg }) => (
  <div style={{
    background: bg || '#f9fafb',
    borderRadius: '12px',
    padding: '18px 22px',
    minWidth: '120px',
    borderLeft: `4px solid ${color}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  }}>
    <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </span>
    <span style={{ fontSize: '28px', fontWeight: 800, color: color }}>
      {value ?? '—'}
    </span>
  </div>
);

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ meta, onPageChange }) => {
  if (!meta || meta.total_pages <= 1) return null;
  const { page, total_pages, total, per_page } = meta;
  const from = (page - 1) * per_page + 1;
  const to   = Math.min(page * per_page, total);

  const pages = [];
  for (let i = 1; i <= total_pages; i++) {
    if (i === 1 || i === total_pages || Math.abs(i - page) <= 1) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div className="pagination-wrapper">
      <span className="pagination-info">
        Showing {from}–{to} of {total} test drives
      </span>
      <div className="pagination-controls">
        <button
          className="page-btn"
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
        >‹</button>
        {pages.map((p, i) =>
          p === '...'
            ? <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
            : <button
                key={p}
                className={`page-btn ${p === page ? 'active' : ''}`}
                onClick={() => onPageChange(p)}
              >{p}</button>
        )}
        <button
          className="page-btn"
          disabled={page === total_pages}
          onClick={() => onPageChange(page + 1)}
        >›</button>
      </div>
    </div>
  );
};

// ─── Status Update Modal ──────────────────────────────────────────────────────
const StatusModal = ({ testDrive, onClose, onSuccess }) => {
  const [status, setStatus]       = useState(testDrive.status);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (status === testDrive.status) { onClose(); return; }
    try {
      setSubmitting(true);
      await adminTestDriveService.updateStatus(testDrive.id, status);
      onSuccess();
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Update Test Drive Status</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 16, padding: 16, background: '#f9fafb', borderRadius: 10 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#111' }}>
              {testDrive.car?.brand} {testDrive.car?.model} ({testDrive.car?.year})
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {testDrive.user?.name} • {testDrive.user?.email}
            </p>
          </div>
          <div className="form-group">
            <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>New Status</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <label key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                  border: `2px solid ${status === key ? meta.color : '#e5e7eb'}`,
                  background: status === key ? `${meta.color}10` : 'white',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="radio"
                    name="status"
                    value={key}
                    checked={status === key}
                    onChange={() => setStatus(key)}
                    style={{ accentColor: meta.color }}
                  />
                  <span style={{ fontWeight: 600, color: meta.color, textTransform: 'capitalize' }}>
                    {meta.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-modal-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn-modal-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? '⏳ Updating...' : '✅ Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const DetailModal = ({ testDrive, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h2>🚗 Test Drive Details #{testDrive.id}</h2>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">
        <div className="details-grid">
          <div className="detail-item">
            <label>Car</label>
            <p>{testDrive.car?.brand} {testDrive.car?.model} ({testDrive.car?.year})</p>
          </div>
          <div className="detail-item">
            <label>Price</label>
            <p>₹{testDrive.car?.price?.toLocaleString() || '—'}</p>
          </div>
          <div className="detail-item">
            <label>User</label>
            <p>{testDrive.user?.name || '—'}</p>
          </div>
          <div className="detail-item">
            <label>Email</label>
            <p>{testDrive.user?.email || '—'}</p>
          </div>
          <div className="detail-item">
            <label>Phone</label>
            <p>{testDrive.user?.phone || '—'}</p>
          </div>
          <div className="detail-item">
            <label>Mode</label>
            <p style={{ textTransform: 'capitalize' }}>{testDrive.mode}</p>
          </div>
          <div className="detail-item">
            <label>Status</label>
            <p><StatusBadge status={testDrive.status} /></p>
          </div>
          <div className="detail-item">
            <label>Preferred Date</label>
            <p>{testDrive.preferred_date ? new Date(testDrive.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</p>
          </div>
          <div className="detail-item">
            <label>Preferred Time</label>
            <p style={{ textTransform: 'capitalize' }}>{testDrive.preferred_time || '—'}</p>
          </div>
          <div className="detail-item">
            <label>Location</label>
            <p>{testDrive.location || '—'}</p>
          </div>
          <div className="detail-item">
            <label>Booked On</label>
            <p>{testDrive.created_at ? new Date(testDrive.created_at).toLocaleString('en-IN') : '—'}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const TestDriveManagement = () => {
  const [testDrives, setTestDrives]     = useState([]);
  const [stats, setStats]               = useState({});
  const [meta, setMeta]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMode, setFilterMode]     = useState('all');
  const [currentPage, setCurrentPage]   = useState(1);
  const [selectedTD, setSelectedTD]     = useState(null);
  const [modalType, setModalType]       = useState('');

  const loadTestDrives = useCallback(async (page = currentPage) => {
    try {
      setLoading(true);
      const data = await adminTestDriveService.getAllTestDrives({
        page,
        per_page: 15,
        status:   filterStatus !== 'all' ? filterStatus : undefined,
        mode:     filterMode   !== 'all' ? filterMode   : undefined,
        search:   searchTerm   || undefined,
      });
      setTestDrives(data.test_drives || []);
      setStats(data.stats || {});
      setMeta(data.meta || null);
    } catch (err) {
      console.error('Failed to load test drives:', err);
      alert('Failed to load test drives: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, filterMode, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterMode, searchTerm]);

  useEffect(() => {
    loadTestDrives(currentPage);
  }, [currentPage, filterStatus, filterMode]); // search on submit

  const handleSearch = (e) => {
    if (e.key === 'Enter') loadTestDrives(1);
  };

  const openModal = (td, type) => { setSelectedTD(td); setModalType(type); };
  const closeModal = () => { setSelectedTD(null); setModalType(''); };

  const handleStatusSuccess = () => {
    closeModal();
    loadTestDrives(currentPage);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test drive?')) return;
    try {
      await adminTestDriveService.deleteTestDrive(id);
      loadTestDrives(currentPage);
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  const statusTabs = ['all', 'requested', 'confirmed', 'completed', 'cancelled'];

  return (
    <div className="inspections-management">
      {/* Header */}
      <div className="management-header">
        <div>
          <h1>🚗 Test Drive Management</h1>
          <p>View and manage all test drives booked by users</p>
        </div>
        <button className="refresh-btn" onClick={() => loadTestDrives(currentPage)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M21.5 2V8M21.5 8H15.5M21.5 8L18.5 5C17.2 3.7 15.4 2.8 13.5 2.3C9.5 1.3 5.5 2.9 3.5 6.3C1.5 9.7 1.8 14 4.5 17M2.5 22V16M2.5 16H8.5M2.5 16L5.5 19C6.8 20.3 8.6 21.2 10.5 21.7C14.5 22.7 18.5 21.1 20.5 17.7C22.5 14.3 22.2 10 19.5 7"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total"     value={stats.total}     color="#003566" bg="#f0f4ff" />
        <StatCard label="Requested" value={stats.requested} color="#f59e0b" bg="#fffbeb" />
        <StatCard label="Confirmed" value={stats.confirmed} color="#3b82f6" bg="#eff6ff" />
        <StatCard label="Completed" value={stats.completed} color="#10b981" bg="#f0fdf4" />
        <StatCard label="Cancelled" value={stats.cancelled} color="#ef4444" bg="#fef2f2" />
        <StatCard label="Home Mode" value={stats.home}      color="#7c3aed" bg="#f5f3ff" />
        <StatCard label="Hub Mode"  value={stats.hub}       color="#0369a1" bg="#e0f2fe" />
      </div>

      {/* Filters */}
      <div className="management-filters">
        {/* Search */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: 260 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by user name, email, car brand/model…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleSearch}
            />
          </div>

          {/* Mode filter */}
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'home', 'hub'].map(m => (
              <button
                key={m}
                className={`filter-tab ${filterMode === m ? 'active' : ''}`}
                onClick={() => setFilterMode(m)}
                style={{ textTransform: 'capitalize' }}
              >
                {m === 'all' ? 'All Modes' : m === 'home' ? '🏠 Home' : '🏢 Hub'}
              </button>
            ))}
          </div>
        </div>

        {/* Status tabs */}
        <div className="filter-tabs">
          {statusTabs.map(s => (
            <button
              key={s}
              className={`filter-tab ${filterStatus === s ? 'active' : ''}`}
              onClick={() => setFilterStatus(s)}
              style={{ textTransform: 'capitalize' }}
            >
              {s === 'all' ? 'All' : STATUS_META[s]?.label || s}
              <span className="filter-count">
                {s === 'all' ? (stats.total || 0) : (stats[s] || 0)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="management-loading">
          <div className="spinner-large" />
          <p>Loading test drives…</p>
        </div>
      ) : (
        <div className="management-table-wrapper">
          <div className="management-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 55 }}>ID</th>
                  <th style={{ width: 180 }}>User</th>
                  <th style={{ width: 200 }}>Car</th>
                  <th style={{ width: 100 }}>Mode</th>
                  <th style={{ width: 130 }}>Date</th>
                  <th style={{ width: 100 }}>Time</th>
                  <th style={{ width: 160 }}>Location</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 130 }}>Booked On</th>
                  <th style={{ width: 130, minWidth: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {testDrives.length === 0 ? (
                  <tr>
                    <td colSpan={10}>
                      <div className="no-results">
                        <p>No test drives found</p>
                      </div>
                    </td>
                  </tr>
                ) : testDrives.map(td => (
                  <tr key={td.id}>
                    <td><strong style={{ color: '#003566' }}>#{td.id}</strong></td>
                    <td>
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#111' }}>
                          {td.user?.name || '—'}
                        </p>
                        <small style={{ color: '#6b7280', fontSize: 11 }}>
                          {td.user?.email}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="car-details">
                        <h4>{td.car?.brand} {td.car?.model}</h4>
                        <p>{td.car?.year}{td.car?.price ? ` • ₹${td.car.price.toLocaleString()}` : ''}</p>
                      </div>
                    </td>
                    <td><ModeBadge mode={td.mode} /></td>
                    <td style={{ fontSize: 13 }}>
                      {td.preferred_date
                        ? new Date(td.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td style={{ fontSize: 13, textTransform: 'capitalize' }}>
                      {td.preferred_time || '—'}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      📍 {td.location || '—'}
                    </td>
                    <td><StatusBadge status={td.status} /></td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {td.created_at ? new Date(td.created_at).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {/* View */}
                        <button
                          className="btn-view"
                          title="View Details"
                          onClick={() => openModal(td, 'detail')}
                        >👁️</button>

                        {/* Change status */}
                        {td.status !== 'cancelled' && td.status !== 'completed' && (
                          <button
                            className="btn-approve"
                            title="Update Status"
                            onClick={() => openModal(td, 'status')}
                          >✏️</button>
                        )}

                        {/* Cancel shortcut */}
                        {(td.status === 'requested' || td.status === 'confirmed') && (
                          <button
                            className="btn-reject"
                            title="Cancel Test Drive"
                            onClick={async () => {
                              if (!window.confirm('Cancel this test drive?')) return;
                              try {
                                await adminTestDriveService.updateStatus(td.id, 'cancelled');
                                loadTestDrives(currentPage);
                              } catch (err) {
                                alert('Failed: ' + (err.response?.data?.error || err.message));
                              }
                            }}
                          >✕</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination meta={meta} onPageChange={p => setCurrentPage(p)} />
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedTD && modalType === 'detail' && (
        <DetailModal testDrive={selectedTD} onClose={closeModal} />
      )}
      {selectedTD && modalType === 'status' && (
        <StatusModal testDrive={selectedTD} onClose={closeModal} onSuccess={handleStatusSuccess} />
      )}
    </div>
  );
};

export default TestDriveManagement;